'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useShallow } from 'zustand/shallow';
import {
  Select,
  Combobox,
  Input,
  Text,
  IconButton,
  createSelectListCollection,
} from '@terra/ui-design-system';
import { VStack, HStack } from '@terra/ui-styled-system';
import { Tick01Icon, ArrowUpDownIcon } from '@terra/ui-icons';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { useTargets } from '../../hooks/useTargets';
import { useAutoSelectCompany } from '../../hooks/useAutoSelectCompany';
import { useProjectStore, type PortalTarget } from '../../store/projectStore';
import {
  formatTargetScopeKey,
  getTargetTypeDisplay,
  parseTargetScopeKey,
  TARGET_TYPE_ORDER,
} from '../../utils/target-scope.utils';

type TargetScopePickerProps = {
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  layout?: 'vertical' | 'horizontal';
  targetInput?: 'select' | 'combobox';
  onTargetSelected?: (target: PortalTarget) => void;
};

export function TargetScopePicker({
  disabled,
  size = 'sm',
  layout = 'vertical',
  targetInput = 'select',
  onTargetSelected,
}: TargetScopePickerProps) {
  const labelFontSize = size === 'sm' ? 'xs' : 'sm';
  const iconSize = size === 'sm' ? 14 : 16;
  const fieldGap = size === 'sm' ? '1' : '1.5';
  const tSelector = useTranslations('proxy.applicationSelector');
  const tCommon = useTranslations('common');
  const { isCompanyReady, isResolvingCompany } = useAutoSelectCompany();
  const { companyId, targetId, targetType, setTarget } = useProjectStore(
    useShallow((state) => ({
      companyId: state.companyId,
      targetId: state.targetId,
      targetType: state.targetType,
      setTarget: state.setTarget,
    })),
  );
  const { targets, isLoading, error } = useTargets({ companyId });
  const [targetInputValue, setTargetInputValue] = useState('');
  const [selectedTargetType, setSelectedTargetType] =
    useState<TargetTypeType>(targetType);

  useEffect(() => {
    setSelectedTargetType(targetType);
  }, [targetType]);
  const availableTargetTypes = useMemo(
    () =>
      TARGET_TYPE_ORDER.filter((type) =>
        targets.some((target) => target.targetType === type),
      ),
    [targets],
  );
  const targetsForSelectedType = useMemo(
    () => targets.filter((target) => target.targetType === selectedTargetType),
    [targets, selectedTargetType],
  );
  const selectedTarget = useMemo(
    () =>
      targets.find(
        (target) => target.id === targetId && target.targetType === targetType,
      ),
    [targets, targetId, targetType],
  );
  const selectedScopeKey = formatTargetScopeKey({ targetId, targetType });
  const selectedTargetName = selectedTarget?.name ?? '';

  useEffect(() => {
    setTargetInputValue(selectedTargetName);
  }, [selectedTargetName]);
  const targetTypeCollection = useMemo(
    () =>
      createSelectListCollection({
        items: availableTargetTypes.map((type) => ({
          label: getTargetTypeDisplay(type).label,
          value: type,
        })),
      }),
    [availableTargetTypes],
  );
  const targetCollection = useMemo(() => {
    const searchLower = targetInputValue.toLowerCase();
    const isSearchingDifferentThanSelected =
      targetInputValue && targetInputValue !== selectedTargetName;
    const filteredItems = targetsForSelectedType
      .filter((target) =>
        targetInput === 'combobox' && isSearchingDifferentThanSelected
          ? target.name.toLowerCase().includes(searchLower)
          : true,
      )
      .map((target) => ({
        label: target.name,
        value: formatTargetScopeKey({
          targetId: target.id,
          targetType: target.targetType,
        }),
        ...target,
      }));

    return createSelectListCollection({ items: filteredItems });
  }, [
    targetsForSelectedType,
    targetInput,
    targetInputValue,
    selectedTargetName,
  ]);
  const applyTargetSelection = useCallback(
    (nextTarget: PortalTarget) => {
      setTarget(nextTarget);
      setSelectedTargetType(nextTarget.targetType);
      setTargetInputValue(nextTarget.name);
      onTargetSelected?.(nextTarget);
    },
    [onTargetSelected, setTarget],
  );
  const handleTargetTypeChange = useCallback(
    (value: string) => {
      const nextType = value as TargetTypeType;

      setSelectedTargetType(nextType);
      const currentMatchesType =
        selectedTarget?.targetType === nextType
          ? selectedTarget
          : targets.find((target) => target.targetType === nextType);

      if (currentMatchesType) {
        applyTargetSelection(currentMatchesType);

        return;
      }

      setTargetInputValue('');
    },
    [applyTargetSelection, selectedTarget, targets],
  );
  const handleTargetChange = useCallback(
    (value: string) => {
      const parsedScope = parseTargetScopeKey(value);

      if (!parsedScope) {
        return;
      }

      const nextTarget = targets.find(
        (target) =>
          target.id === parsedScope.targetId &&
          target.targetType === parsedScope.targetType,
      );

      if (nextTarget) {
        applyTargetSelection(nextTarget);
      }
    },
    [applyTargetSelection, targets],
  );
  const handleComboboxValueChange = useCallback(
    (details: { value: string[] }) => {
      handleTargetChange(details.value[0] ?? '');
    },
    [handleTargetChange],
  );
  const isDisabled =
    disabled ||
    isLoading ||
    isResolvingCompany ||
    !isCompanyReady ||
    !companyId;
  const targetPlaceholder = isResolvingCompany
    ? tSelector('preparingWorkspace')
    : !isCompanyReady
      ? tSelector('workspaceNotReady')
      : !companyId
        ? 'Select a company first...'
        : tSelector('searchApplications');

  if (error) {
    return (
      <Text color="fg.error" fontSize="sm">
        {tSelector('applicationsLoadError')}
      </Text>
    );
  }

  const showTargetTypePicker = availableTargetTypes.length > 1;
  const typeSelectField = showTargetTypePicker ? (
    <VStack
      gap={fieldGap}
      alignItems="stretch"
      flex={layout === 'horizontal' ? '0 0 160px' : undefined}
      width={layout === 'horizontal' ? undefined : 'full'}
    >
      <Text fontSize={labelFontSize} fontWeight="medium" textAlign="left">
        {tSelector('targetTypeLabel')}
      </Text>
      <Select.Root
        size={size}
        positioning={{ sameWidth: true, strategy: 'fixed' }}
        width="full"
        value={
          availableTargetTypes.includes(selectedTargetType)
            ? [selectedTargetType]
            : []
        }
        collection={targetTypeCollection}
        onValueChange={(event) => handleTargetTypeChange(event.value[0])}
        disabled={isDisabled || availableTargetTypes.length === 0}
      >
        <Select.Control>
          <Select.Trigger aria-label={tSelector('selectTargetTypePlaceholder')}>
            <Select.ValueText
              placeholder={tSelector('selectTargetTypePlaceholder')}
            />
            <ArrowUpDownIcon size={iconSize} aria-hidden="true" />
          </Select.Trigger>
        </Select.Control>
        <Select.Positioner>
          <Select.Content maxHeight="240px" overflowY="auto">
            {targetTypeCollection.items.map((item) => (
              <Select.Item key={item.value} item={item}>
                <Select.ItemText>{item.label}</Select.ItemText>
                <Select.ItemIndicator>
                  <Tick01Icon size={iconSize} aria-hidden="true" />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Select.Root>
    </VStack>
  ) : null;
  const targetSelectField =
    targetInput === 'combobox' ? (
      <VStack
        gap={fieldGap}
        alignItems="stretch"
        flex={layout === 'horizontal' ? 1 : undefined}
        width={layout === 'horizontal' ? undefined : 'full'}
        minW="0"
      >
        <Text fontSize={labelFontSize} fontWeight="medium" textAlign="left">
          {tSelector('applicationLabel')}
        </Text>
        <Combobox.Root
          collection={targetCollection}
          value={targetId ? [selectedScopeKey] : []}
          onValueChange={handleComboboxValueChange}
          onInputValueChange={(details) =>
            setTargetInputValue(details.inputValue)
          }
          inputValue={targetInputValue}
          width="full"
          size={size}
          disabled={isDisabled || targetsForSelectedType.length === 0}
          positioning={{ sameWidth: true, strategy: 'fixed' }}
          openOnClick
        >
          <Combobox.Control>
            <Combobox.Input placeholder={targetPlaceholder} asChild>
              <Input size={size} />
            </Combobox.Input>
            <Combobox.Trigger asChild>
              <IconButton
                variant="link"
                aria-label={tSelector('selectApplicationAriaLabel')}
                size="xs"
              >
                <ArrowUpDownIcon size={iconSize} />
              </IconButton>
            </Combobox.Trigger>
          </Combobox.Control>
          <Combobox.Positioner>
            <Combobox.Content maxH="300px" overflowY="auto">
              <Combobox.List>
                {isLoading ? (
                  <Text fontSize="xs" color="fg.muted" p={2} textAlign="center">
                    {tCommon('loading')}
                  </Text>
                ) : targetCollection.items.length > 0 ? (
                  targetCollection.items.map((item) => (
                    <Combobox.Item key={item.value} item={item}>
                      <Combobox.ItemText>{item.label}</Combobox.ItemText>
                      <Combobox.ItemIndicator>
                        <Tick01Icon size={iconSize} />
                      </Combobox.ItemIndicator>
                    </Combobox.Item>
                  ))
                ) : (
                  <Text fontSize="xs" color="fg.muted" p={2} textAlign="center">
                    {tSelector('noApplicationsFound')}
                  </Text>
                )}
              </Combobox.List>
            </Combobox.Content>
          </Combobox.Positioner>
        </Combobox.Root>
      </VStack>
    ) : (
      <VStack
        gap={fieldGap}
        alignItems="stretch"
        flex={layout === 'horizontal' ? 1 : undefined}
        width={layout === 'horizontal' ? undefined : 'full'}
        minW="0"
      >
        <Text fontSize={labelFontSize} fontWeight="medium" textAlign="left">
          {tSelector('applicationLabel')}
        </Text>
        <Select.Root
          size={size}
          positioning={{ sameWidth: true, strategy: 'fixed' }}
          width="full"
          value={targetId ? [selectedScopeKey] : []}
          collection={targetCollection}
          onValueChange={(event) => handleTargetChange(event.value[0])}
          disabled={isDisabled || targetsForSelectedType.length === 0}
        >
          <Select.Control>
            <Select.Trigger
              aria-label={tSelector('selectApplicationAriaLabel')}
            >
              <Select.ValueText placeholder={targetPlaceholder} />
              <ArrowUpDownIcon size={iconSize} aria-hidden="true" />
            </Select.Trigger>
          </Select.Control>
          <Select.Positioner>
            <Select.Content maxHeight="300px" overflowY="auto">
              {targetCollection.items.map((item) => (
                <Select.Item key={item.value} item={item}>
                  <Select.ItemText>{item.label}</Select.ItemText>
                  <Select.ItemIndicator>
                    <Tick01Icon size={iconSize} aria-hidden="true" />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      </VStack>
    );

  if (layout === 'horizontal') {
    return (
      <HStack gap="3" alignItems="flex-end" width="full">
        {showTargetTypePicker ? typeSelectField : null}
        {targetSelectField}
      </HStack>
    );
  }

  return (
    <VStack gap="3" alignItems="stretch" width="full">
      {showTargetTypePicker ? typeSelectField : null}
      {targetSelectField}
    </VStack>
  );
}
